import * as THREE from 'three';

export type ActionName = 'jump' | 'wind' | 'interact' | 'pause' | 'hideUi' | 'walkToggle' | 'slash' | 'equip';

const DEADZONE = 0.14;
const applyDeadzone = (v: number) => (Math.abs(v) < DEADZONE ? 0 : (v - Math.sign(v) * DEADZONE) / (1 - DEADZONE));

export class Input {
  private readonly keys = new Set<string>();
  private readonly pressed = new Set<ActionName>();
  private lookX = 0;
  private lookY = 0;
  private dragging = false;
  private readonly touchMove = new THREE.Vector2();
  private touchSprint = false;
  private touchCrouch = false;
  private gamepadCrouch = false;
  private gamepadSprint = false;
  private readonly gamepadMove = new THREE.Vector2();
  private readonly gamepadLook = new THREE.Vector2();
  private readonly gamepadButtons: boolean[] = [];
  enabled = false;
  pointerLocked = false;
  sensitivity = 1;
  invertY = false;
  /** Last input device family, so the UI can show matching hints. */
  device: 'keyboard' | 'gamepad' | 'touch' = 'keyboard';
  lookActivity = 0;
  onPointerLockChange: (locked: boolean) => void = () => {};

  constructor(private readonly canvas: HTMLCanvasElement) {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
    canvas.addEventListener('mousedown', this.onMouseDown);
    window.addEventListener('mouseup', this.onMouseUp);
    window.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('pointerlockchange', this.onLockChange);
    canvas.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  requestPointerLock(): void {
    if (this.device === 'touch') return;
    try {
      const result = this.canvas.requestPointerLock() as unknown as Promise<void> | undefined;
      result?.catch?.(() => {});
    } catch {
      // Pointer lock can be unavailable (iframes, automation); drag-to-look still works.
    }
  }

  exitPointerLock(): void {
    if (document.pointerLockElement) document.exitPointerLock();
  }

  private readonly onLockChange = () => {
    this.pointerLocked = document.pointerLockElement === this.canvas;
    this.onPointerLockChange(this.pointerLocked);
  };

  private readonly onKeyDown = (e: KeyboardEvent) => {
    if (e.repeat) {
      if (['Space', 'ArrowUp', 'ArrowDown'].includes(e.code)) e.preventDefault();
      return;
    }
    this.device = 'keyboard';
    this.keys.add(e.code);
    if (e.code === 'Space') {
      this.pressed.add('jump');
      e.preventDefault();
    }
    if (e.code === 'KeyQ') this.pressed.add('wind');
    if (e.code === 'KeyF') this.pressed.add('slash');
    if (e.code === 'Digit1' || e.code === 'Numpad1') this.pressed.add('equip');
    if (e.code === 'KeyE') this.pressed.add('interact');
    if (e.code === 'KeyH') this.pressed.add('hideUi');
    if (e.code === 'CapsLock') this.pressed.add('walkToggle');
    if (e.code === 'Escape' || e.code === 'KeyP') this.pressed.add('pause');
  };

  private readonly onKeyUp = (e: KeyboardEvent) => {
    this.keys.delete(e.code);
  };

  private readonly onBlur = () => {
    this.keys.clear();
    this.dragging = false;
  };

  private readonly onMouseDown = (e: MouseEvent) => {
    this.device = 'keyboard';
    if (e.button !== 0) return;
    // Locked: the click is the sword. Unlocked: it starts a drag-look (and the game requests the lock).
    if (this.pointerLocked) {
      if (this.enabled) this.pressed.add('slash');
    } else {
      this.dragging = true;
    }
  };

  private readonly onMouseUp = () => {
    this.dragging = false;
  };

  private readonly onMouseMove = (e: MouseEvent) => {
    if (!this.enabled) return;
    if (this.pointerLocked || this.dragging) {
      this.lookX += e.movementX;
      this.lookY += e.movementY;
      this.lookActivity = 0;
    }
  };

  /** Touch UI hooks. */
  setTouchMove(x: number, y: number): void {
    this.device = 'touch';
    this.touchMove.set(x, y);
  }

  addTouchLook(dx: number, dy: number): void {
    this.device = 'touch';
    this.lookX += dx * 1.6;
    this.lookY += dy * 1.6;
    this.lookActivity = 0;
  }

  setTouchCrouch(on: boolean): void {
    this.touchCrouch = on;
  }

  setTouchSprint(on: boolean): void {
    this.touchSprint = on;
  }

  get touchSprinting(): boolean {
    return this.touchSprint;
  }

  press(action: ActionName): void {
    this.pressed.add(action);
  }

  /** Polls gamepads; call once per frame before reading. */
  poll(dt: number): void {
    this.lookActivity += dt;
    const pads = navigator.getGamepads ? navigator.getGamepads() : [];
    const pad = Array.from(pads).find((p) => p && p.connected);
    if (!pad) {
      this.gamepadMove.set(0, 0);
      this.gamepadLook.set(0, 0);
      return;
    }
    const ax = (i: number) => applyDeadzone(pad.axes[i] ?? 0);
    this.gamepadMove.set(ax(0), -ax(1));
    this.gamepadLook.set(ax(2), ax(3));
    if (this.gamepadMove.lengthSq() > 0 || this.gamepadLook.lengthSq() > 0) this.device = 'gamepad';
    if (this.gamepadLook.lengthSq() > 0) this.lookActivity = 0;
    const btn = (i: number) => Boolean(pad.buttons[i]?.pressed);
    const edge = (i: number, action: ActionName) => {
      const now = btn(i);
      if (now && !this.gamepadButtons[i]) {
        this.pressed.add(action);
        this.device = 'gamepad';
      }
      this.gamepadButtons[i] = now;
    };
    edge(0, 'jump');
    edge(1, 'slash');
    edge(12, 'equip');
    edge(3, 'wind');
    edge(2, 'interact');
    edge(9, 'pause');
    edge(8, 'hideUi');
    this.gamepadSprint = btn(10) || btn(4) || btn(6) || btn(5) || btn(7);
    this.gamepadCrouch = btn(11);
  }

  /** x = right, y = forward, length <= 1. */
  readMove(target: THREE.Vector2): THREE.Vector2 {
    target.set(0, 0);
    if (!this.enabled) return target;
    if (this.keys.has('KeyA') || this.keys.has('ArrowLeft')) target.x -= 1;
    if (this.keys.has('KeyD') || this.keys.has('ArrowRight')) target.x += 1;
    if (this.keys.has('KeyW') || this.keys.has('ArrowUp')) target.y += 1;
    if (this.keys.has('KeyS') || this.keys.has('ArrowDown')) target.y -= 1;
    target.add(this.gamepadMove).add(this.touchMove);
    if (target.lengthSq() > 1) target.normalize();
    return target;
  }

  /** Crouch (C, right stick click, touch 伏); pressed while running it becomes a slide. */
  crouchHeld(): boolean {
    if (!this.enabled) return false;
    return this.keys.has('KeyC') || this.gamepadCrouch || this.touchCrouch;
  }

  sprintHeld(): boolean {
    if (!this.enabled) return false;
    return this.keys.has('ShiftLeft') || this.keys.has('ShiftRight') || this.gamepadSprint || this.touchSprint;
  }

  /** Returns accumulated look delta in pixels-equivalent and resets it. */
  consumeLook(target: THREE.Vector2, dt: number): THREE.Vector2 {
    target.set(this.lookX, this.lookY);
    target.x += this.gamepadLook.x * 900 * dt;
    target.y += this.gamepadLook.y * 520 * dt;
    this.lookX = 0;
    this.lookY = 0;
    target.multiplyScalar(this.sensitivity);
    if (this.invertY) target.y = -target.y;
    if (!this.enabled) target.set(0, 0);
    return target;
  }

  consume(action: ActionName): boolean {
    const had = this.pressed.has(action);
    this.pressed.delete(action);
    return had;
  }

  clearActions(): void {
    this.pressed.clear();
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    this.canvas.removeEventListener('mousedown', this.onMouseDown);
    window.removeEventListener('mouseup', this.onMouseUp);
    window.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('pointerlockchange', this.onLockChange);
  }
}
